import { useState } from 'react';
import { Link } from 'react-router-dom';
import CalendarButton from './CalendarButton';
import { daysOverdue, formatDueLabel } from '../../lib/todos';
import { LINK_TYPES } from '../../lib/todoLinks';

const DAY = new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'short' });

// Two letters from a name or an email. Always rendered, even unassigned, so
// the column doesn't appear and disappear down the list.
export function initials(profile) {
  if (!profile) return null;
  const source = profile.display_name || profile.email || '';
  const parts = source.replace(/@.*/, '').split(/[.\s_-]+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
}

const SNOOZE = [
  { key: 'tomorrow', label: 'Tomorrow', days: 1 },
  { key: 'week', label: 'Next week', days: 7 },
];

/* One task, one row.
 *
 * The old list gave every task a whole card, so two tasks filled a screen
 * that could hold thirty. Columns are fixed-width so the eye can run down
 * the due dates instead of hunting for them at whatever indent the title
 * happened to end.
 */
export default function TaskRow({ task, today, profile, onToggle, onOpen, onSnooze, onSomeday, onDuplicate, onDelete, onCalendarAdded }) {
  const [menu, setMenu] = useState(false);
  const done = task.status === 'Done';
  const late = !done && task.due_date && task.due_date < today;
  const lateBy = late ? daysOverdue(task.due_date, today) : 0;
  const link = task.link_type ? LINK_TYPES[task.link_type] : null;

  return (
    <li className={'task-row' + (done ? ' done' : '') + ` priority-${(task.priority || 'Normal').toLowerCase()}`}>
      <label className="task-check">
        <input
          type="checkbox"
          checked={done}
          onChange={() => onToggle(task)}
          aria-label={done ? `Reopen ${task.title}` : `Complete ${task.title}`}
        />
      </label>

      <button className="task-title" onClick={() => onOpen(task)}>
        {task.title}
      </button>

      {link && task.link_label && (
        <Link
          className="task-link-chip"
          to={link.to(task.link_id)}
          onClick={(e) => e.stopPropagation()}
        >
          {task.link_label} · {link.label}
        </Link>
      )}

      <span
        className={'task-avatar' + (profile ? '' : ' empty')}
        title={profile ? profile.display_name || profile.email : 'Unassigned'}
      >
        {initials(profile) || '–'}
      </span>

      <span className="task-due">
        {task.due_date ? (
          <>
            {/* The actual date, not only how late it is: "24 days late"
                tells you it's bad but not when it was meant to happen, which
                is the thing you need to reschedule it. */}
            <span className="task-due-date">{DAY.format(new Date(`${task.due_date}T12:00:00`))}</span>
            {late ? (
              <span className="task-due-late">
                {lateBy} day{lateBy === 1 ? '' : 's'} late
              </span>
            ) : (
              <span className="task-due-rel">{formatDueLabel(task.due_date, today)}</span>
            )}
          </>
        ) : (
          <span className="task-due-rel">—</span>
        )}
      </span>

      <span className="task-calendar">
        <CalendarButton task={task} onAdded={() => onCalendarAdded(task)} />
      </span>

      <span className="task-menu">
        <button
          className="task-dots"
          aria-label={`Actions for ${task.title}`}
          onClick={() => setMenu((v) => !v)}
        >
          ···
        </button>
        {menu && (
          <>
            <div className="lab-menu-scrim" onClick={() => setMenu(false)} />
            <ul className="lab-menu">
              <li>
                <button onClick={() => { setMenu(false); onOpen(task); }}>Edit</button>
              </li>
              {!done &&
                SNOOZE.map((s) => (
                  <li key={s.key}>
                    <button onClick={() => { setMenu(false); onSnooze(task, s.days, s.label); }}>
                      Snooze to {s.label.toLowerCase()}
                    </button>
                  </li>
                ))}
              {!done && (
                <li>
                  <button onClick={() => { setMenu(false); onSomeday(task); }}>
                    {task.is_someday ? 'Move back to To-do' : 'Move to Someday'}
                  </button>
                </li>
              )}
              <li>
                <button onClick={() => { setMenu(false); onDuplicate(task); }}>Duplicate</button>
              </li>
              <li>
                <button onClick={() => { setMenu(false); onDelete(task); }}>Delete</button>
              </li>
            </ul>
          </>
        )}
      </span>
    </li>
  );
}
